import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Search, RefreshCw, ChevronLeft, ChevronRight, Eye, CheckCircle, XCircle, Clock, AlertTriangle, ExternalLink, X } from 'lucide-react';
import api from '../utils/api';

const EmailLogs = () => {
  const [activeTab, setActiveTab] = useState('client');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/email-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        recipient_type: activeTab,
        page: page.toString(),
        limit: '25'
      });
      if (search) {
        params.append('search', search);
      }
      
      const response = await api.get(`/email-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search]);

  const fetchEmailDetail = async (logId) => {
    setLoadingDetail(true);
    try {
      const response = await api.get(`/email-logs/${logId}`);
      setEmailDetail(response.data);
    } catch (error) {
      console.error('Error fetching email detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (selectedEmail) {
      fetchEmailDetail(selectedEmail);
    }
  }, [selectedEmail]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (log) => {
    if (log.status === 'bounced') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Bounced
        </span>
      );
    }
    if (log.status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <AlertTriangle className="w-3 h-3" />
          Failed
        </span>
      );
    }
    if (log.opened_at || log.open_count > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <Eye className="w-3 h-3" />
          Opened {log.open_count > 1 ? `${log.open_count}x` : ''}{log.click_count > 0 ? ` • ${log.click_count} click${log.click_count > 1 ? 's' : ''}` : ''}
        </span>
      );
    }
    if (log.status === 'delivered') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <CheckCircle className="w-3 h-3" />
          Delivered
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        <Clock className="w-3 h-3" />
        Sent
      </span>
    );
  };

  const getEmailTypeBadge = (type) => {
    const typeLabels = {
      'task_notification': 'Task',
      'portal_invite': 'Portal Invite',
      'project_update': 'Project',
      'team_invitation': 'Team Invite',
      'password_reset': 'Password Reset',
      'notification': 'Notification',
      'mfa_code': '2FA Code'
    };
    return typeLabels[type] || type || 'Email';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-purple-600" />
            Email Logs
          </h2>
          <p className="text-gray-500 mt-1">View outbound emails from the last 30 days</p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchLogs(); }}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_sent.toLocaleString()}</div>
            <div className="text-sm text-gray-500">Emails Sent</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-green-600">{stats.delivery_rate}%</div>
            <div className="text-sm text-gray-500">Delivery Rate</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.open_rate}%</div>
            <div className="text-sm text-gray-500">Open Rate</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-red-600">{stats.total_bounced}</div>
            <div className="text-sm text-gray-500">Bounced</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b">
        <button
          onClick={() => handleTabChange('client')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'client'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          👥 Client Emails
          {stats && <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">{stats.client_emails}</span>}
        </button>
        <button
          onClick={() => handleTabChange('internal')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'internal'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🏢 Internal Emails
          {stats && <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">{stats.internal_emails}</span>}
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, name, or subject..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Search
        </button>
      </form>

      {/* Email List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading emails...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No emails found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Recipient</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Subject</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Sent</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{log.recipient_name || 'Unknown'}</div>
                    <div className="text-sm text-gray-500">{log.to_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-900 max-w-xs truncate" title={log.subject}>
                      {log.subject}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      {getEmailTypeBadge(log.email_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(log.sent_at)}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(log)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelectedEmail(log.id)}
                      className="text-purple-600 hover:text-purple-700 p-1"
                      title="View email"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total} emails
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Email Details</h3>
              <button
                onClick={() => { setSelectedEmail(null); setEmailDetail(null); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {loadingDetail ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : emailDetail ? (
              <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Email Metadata */}
                <div className="px-6 py-4 bg-gray-50 border-b space-y-2">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 w-20">To:</span>
                    <span className="font-medium">{emailDetail.recipient_name} &lt;{emailDetail.to_email}&gt;</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 w-20">Subject:</span>
                    <span className="font-medium">{emailDetail.subject}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 w-20">Sent:</span>
                    <span>{emailDetail.sent_at ? new Date(emailDetail.sent_at).toLocaleString() : '-'}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 w-20">Status:</span>
                    {getStatusBadge(emailDetail)}
                  </div>
                  {emailDetail.opened_at && (
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500 w-20">Opened:</span>
                      <span>{new Date(emailDetail.opened_at).toLocaleString()} ({emailDetail.open_count}x)</span>
                    </div>
                  )}
                  {emailDetail.clicked_at && (
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500 w-20">Clicked:</span>
                      <span>{new Date(emailDetail.clicked_at).toLocaleString()} ({emailDetail.click_count}x)</span>
                    </div>
                  )}
                  {emailDetail.error_message && (
                    <div className="flex items-start gap-4">
                      <span className="text-sm text-gray-500 w-20">Error:</span>
                      <span className="text-red-600">{emailDetail.error_message}</span>
                    </div>
                  )}
                </div>
                
                {/* Email Body */}
                <div className="p-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Email Content</h4>
                  {emailDetail.body_html ? (
                    <div 
                      className="border rounded-lg p-4 bg-white prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: emailDetail.body_html }}
                    />
                  ) : (
                    <div className="border rounded-lg p-4 bg-gray-50 text-gray-500 text-center">
                      Email content not available
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">Email not found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLogs;
